<template>
  <main class="marketing-page">
    <section class="marketing-shell">
      <header class="marketing-header">
        <p class="marketing-eyebrow">Marketing</p>
        <h1>Reputation</h1>
        <p class="marketing-subtitle">
          Automatically ask customers for a review once a job is marked complete.
        </p>
      </header>

      <div v-if="loadError" class="banner banner-error">{{ loadError }}</div>

      <div v-if="!hasReviewUrls" class="banner banner-info">
        Add at least one review link below to start sending review requests automatically.
      </div>

      <section class="panel">
        <div class="panel-header">
          <h2>Review Links</h2>
          <p>Shown to a customer's email once their job is marked complete.</p>
        </div>

        <div class="form-grid">
          <label class="field">
            <span>Google review link</span>
            <input class="form-field" type="url" v-model="form.google_review_url" placeholder="https://g.page/r/..." />
          </label>

          <label class="field">
            <span>Facebook review link</span>
            <input class="form-field" type="url" v-model="form.facebook_review_url" placeholder="https://facebook.com/..." />
          </label>

          <label class="field">
            <span>Current Google rating (manual)</span>
            <input class="form-field" type="number" step="0.1" min="0" max="5" v-model="form.google_rating" placeholder="4.8" />
          </label>

          <label class="field">
            <span>Current Google review count (manual)</span>
            <input class="form-field" type="number" step="1" min="0" v-model="form.google_review_count" placeholder="132" />
          </label>
        </div>

        <div class="form-actions">
          <button class="save-button" type="button" @click="save" :disabled="saving">
            <v-progress-circular v-if="saving" indeterminate size="18" width="2" />
            <template v-else-if="saveSuccess">Saved</template>
            <template v-else>Save</template>
          </button>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <h2>Requests Sent</h2>
          <p>Every automatic review request, most recent first.</p>
        </div>

        <div v-if="requestsLoading" class="empty-state">
          <v-progress-circular indeterminate size="24" color="primary" />
          <span>Loading…</span>
        </div>

        <table v-else-if="requests.length" class="requests-table">
          <thead>
            <tr>
              <th>Job</th>
              <th>Customer</th>
              <th>Sent</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in requests" :key="row.id">
              <td>{{ row.job_title }}</td>
              <td>{{ row.contact_name || '—' }}</td>
              <td>{{ formatDate(row.sent_at) }}</td>
            </tr>
          </tbody>
        </table>

        <div v-else class="empty-state">
          <v-icon size="28">mdi-star-outline</v-icon>
          <span>No review requests sent yet — this fills in as jobs are marked complete.</span>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { computed, reactive, ref, watchEffect } from 'vue'
import { company, getAuthToken, API_BASE_URL } from '@/stores/appConfig'
import { logAction } from '@/utils/logAction'

const loadError = ref('')
const saving = ref(false)
const saveSuccess = ref(false)

const requests = ref([])
const requestsLoading = ref(false)

const form = reactive({
  google_review_url: '',
  facebook_review_url: '',
  google_rating: '',
  google_review_count: '',
})

watchEffect(() => {
  form.google_review_url = company.google_review_url || ''
  form.facebook_review_url = company.facebook_review_url || ''
  form.google_rating = company.google_rating ?? ''
  form.google_review_count = company.google_review_count ?? ''
})

const hasReviewUrls = computed(() =>
  Boolean(company.google_review_url || company.facebook_review_url)
)

async function authHeaders() {
  const token = await getAuthToken()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function fetchRequests() {
  requestsLoading.value = true

  try {
    const headers = await authHeaders()
    const response = await fetch(
      `${API_BASE_URL}/marketing/reputation?company_id=${encodeURIComponent(company.company_id)}`,
      { headers }
    )
    const data = await response.json().catch(() => ({}))

    if (!response.ok || !data.ok) {
      throw new Error(data.error || 'Failed to load review requests.')
    }

    requests.value = data.requests || []
  } catch (err) {
    loadError.value = err.message || 'Failed to load review requests.'
  } finally {
    requestsLoading.value = false
  }
}

watchEffect(() => {
  if (company.company_id) fetchRequests()
})

async function save() {
  loadError.value = ''
  saving.value = true
  saveSuccess.value = false

  try {
    const token = await getAuthToken()
    const response = await fetch(`${API_BASE_URL}/companies/upsert`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        owner_firebase_uid: company.owner_firebase_uid,
        google_review_url: form.google_review_url || null,
        facebook_review_url: form.facebook_review_url || null,
        google_rating: form.google_rating === '' ? null : Number(form.google_rating),
        google_review_count: form.google_review_count === '' ? null : Number(form.google_review_count),
      }),
    })

    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.error || 'Failed to save review settings.')
    }

    if (data.company) {
      Object.assign(company, data.company)
    }

    saveSuccess.value = true
    logAction(token, {
      action: 'company.update',
      label: 'Updated review settings',
      company_id: company.company_id,
    })

    setTimeout(() => { saveSuccess.value = false }, 3000)
  } catch (err) {
    loadError.value = err.message || 'Failed to save review settings.'
  } finally {
    saving.value = false
  }
}

function formatDate(value) {
  return value ? new Date(value).toLocaleString('en-CA', { hour12: false }) : '—'
}
</script>

<style scoped>
.marketing-page {
  min-height: calc(100dvh - 70px);
  padding: 32px 18px 110px;
  background: var(--surface-alt);
  color: var(--text-primary);
}

.marketing-shell {
  width: min(980px, 100%);
  margin: 0 auto;
  display: grid;
  gap: 20px;
}

.marketing-eyebrow {
  margin: 0 0 6px;
  color: #1f7a63;
  font-size: 0.78rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.marketing-header h1 {
  margin: 0;
  font-size: 2rem;
  font-weight: 850;
  line-height: 1.1;
}

.marketing-subtitle {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
}

.banner {
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 0.86rem;
}

.banner-error {
  color: #8a2b1f;
  background: #fdecea;
  border: 1px solid #f3c6bf;
}

.banner-info {
  color: #1f5c4d;
  background: #eef8f5;
  border: 1px solid #9fc8ba;
}

.panel {
  background: var(--surface);
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  padding: 20px;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 800;
}

.panel-header p {
  margin: 4px 0 16px;
  color: var(--text-secondary);
  font-size: 0.86rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.field {
  display: grid;
  gap: 6px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--text-secondary);
}

.form-field {
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border-subtle);
  border-radius: 8px;
  background: var(--surface-alt);
  color: var(--text-primary);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}

.save-button {
  min-width: 100px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  border: 0;
  border-radius: 8px;
  color: #ffffff;
  background: #f26e2e;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
}

.save-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.requests-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.86rem;
}

.requests-table th {
  text-align: left;
  padding: 8px 10px;
  color: var(--text-secondary);
  font-size: 0.76rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 1px solid var(--border-subtle);
}

.requests-table td {
  padding: 10px;
  border-bottom: 1px solid var(--border-subtle);
}

@media (max-width: 620px) {
  .marketing-page {
    padding: 22px 14px 110px;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }
}
</style>
